import {
  BriefDeliverableStatusEnum,
  BriefDeliverableTaskTypeEnum,
  BriefDeliverableTaskUploadTypeEnum,
  SORT_ORDER,
  UploaderType,
  scriptingStatusConst,
} from "@entities/brief/lib/constants";
import { IBrief } from "./brief";
import { IBrandComment } from "./briefInfluencer";
import {
  IAssignedPOC,
  IBriefDeliverableResponse,
  IBriefInfluencerV2,
  IDataFieldValue,
} from "./briefInfluencerV2";
import { IPurchaseItems } from "./deliverableConfigurator";
import { IComplianceReport, IDrafting } from "./drafting";
import { Pagination } from "./influencer";
import { ReportMedia } from "./reports";

export interface BriefCampaignInfs {
  data: BriefCampaignInf[];
  pagination: Pagination;
}

export interface IBriefCampaignBriefInfluencer extends Omit<
  IBriefInfluencerV2,
  "deliverable"
> {
  deliverable?: IBriefDeliverableResponse["deliverableV2"];
}

export interface BriefCampaignInf {
  dynamicFieldsFromBriefInfluencer: { [key: string]: IDataFieldValue };
  brandComments: IBrandComment[];
  assignedOperationsPoc?: IAssignedPOC;
  _id: string;
  briefId: string;
  brief: IBrief;
  briefInfluencerId: string;
  briefInfluencer: IBriefCampaignBriefInfluencer;
  socialMediaObjectId: string;
  socialMedia: {
    _id: string;
    platform: string;
    username: string;

    link: string;
    searchV1: {
      fullName: string;
      followerCount: number;
      isVerified: boolean;
    };
    profilePicUrl: string;
  };
  influencerId: string;
  influencer: {
    _id: string;
    firstName: string;
    lastName: string;
    type: string;
    isPrimaryContact: boolean;
    phoneNumber: string;
    countryCode: string;
    contacts: any[];
    isAgency: boolean;
    category: string[];
    location: any[];
    avgReview: number;
    profilePicUrl: string;
    emailId: string;
  };
  influencerName: string;
  socialMediaName: string;
  socialMediaLink: string;
  deliverables: ICampaignDeliverable[];
  isVendorBillingAttached: boolean;
  createdBy: string;
  createdByModelName: string;
  createdByType: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  dynamicFields: {
    [key: string]: string | number;
  };
  isCreatorDeclined: boolean;
  isBrandDeclined: boolean;
  brandDeclinedReason?: string;
  creatorDeclinedReason?: string;
  financeFlags?: {
    invoice?: {
      status?: FinanceInvoiceFlags;
      paid?: number;
      total?: number;
    };
    basePayout?: {
      status?: FinanceBasePayoutFlags;
      paid?: number;
      total?: number;
    };
    gstPayout?: {
      status?: FinanceGstPayoutFlags;
      paid?: number;
      total?: number;
    };
  };
  vendorBilling?: BillingEntity;
  purchaseItems?: IPurchaseItems[];
}

export enum FinanceInvoiceFlags {
  Pending = "Pending",
  PartiallyPaid = "Partially-Paid",
  Paid = "Paid",
  NA = "N/A",
}
export enum FinanceBasePayoutFlags {
  Pending = "Pending",
  PartiallyPaid = "Partially-Paid",
  Paid = "Paid",
  NA = "N/A",
}
export enum FinanceGstPayoutFlags {
  Pending = "Pending",
  PartiallyPaid = "Partially-Paid",
  Paid = "Paid",
  NA = "N/A",
}

export interface ICreateVendorBilling {
  type: "Influencer" | "Agency";
  billingType: "Individual" | "Company";
  phoneNumber?: string;
  countryCode?: string;
  emailId?: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pinCode: string;
  country: string;
  isPan: boolean;
  panNumber: string;
  tdsPercentage: number;
  isGst?: boolean;
  gstNumber?: string;
  gstPercentage?: number;
  isMsme?: boolean;
  msmeNumber?: string;
}

export interface IGetVendorBilling {
  self: VendorBillingSelf[];
  linkedAgencies: VendorBillingLinkedAgency[];
}

export interface VendorBillingSelf {
  billingEntity: BillingEntity;
  influencer: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicUrl: string;
  };
  isBillingFound: boolean;
}

export interface VendorBillingLinkedAgency {
  agency: {
    _id: string;
    name: string;
    profilePicUrl?: string;
  };
  billingEntity: BillingEntity | null;
  isBillingFound: boolean;
}

export interface BillingEntity {
  _id: string;
  type: string;
  influencerId: string;
  influencer: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicUrl: string;
  };
  phoneNumber: string;
  countryCode: string;
  emailId: string;
  isInternational: boolean;
  billingType: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pinCode: string;
  country: string;
  isPan: boolean;
  panNumber: string;
  tdsPercentage: number;
  isGst: boolean;
  gstNumber: string;
  gstPercentage: number;
  isMsme: boolean;
  msmeNumber: string;
  isArchived: boolean;
  updatedBy: string;
  createdBy: string;
  updatedByType: string;
  createdByType: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  agency?: { name: string };
  panVerification?: {
    _id: string;
    pan: string;
    status: "Valid" | "Invalid";
    createdBy: string;
    createdByModelName: string;
    createdByType: string;
    verificationSource: string;
    verificationResponse: {
      pan: string;
      type: string;
      reference_id: number;
      name_provided: string;
      registered_name: string;
      father_name: string;
      valid: boolean;
    };
  };
}

export interface IFinanceInfluencer {
  campaignInfluencer: {
    _id: string;
    briefId: string;
    brief: string;
    briefInfluencerId: string;
    briefInfluencer: string;
    socialMediaObjectId: string;
    socialMedia: {
      _id: string;
      platform: string;
      username: string;
      link: string;
      searchV1: {
        fullName: string;
        followerCount: number;
        isVerified: boolean;
      };
      profilePicUrl: string;
    };
    influencerId: string;
    influencer: {
      _id: string;
      firstName: string;
      lastName: string;
      type: string;
      isPrimaryContact: boolean;
      contacts: Array<{
        name: string;
        type: string;
        phoneNumber: string;
        countryCode: string;
        emailId: string;
        isPrimaryContact: boolean;
        influencerContactId: string;
        agency?: {
          _id: string;
          name: string;
          companyName: string;
          profilePicUrl: string;
          agencyId: string;
        };
      }>;
      isAgency: boolean;
      agency?: {
        _id: string;
        name: string;
        companyName: string;
        profilePicUrl: string;
        agencyId: string;
      };
      category: string[];
      location: string[];
      avgReview: number;
      influencerId: string;
    };
    influencerName: string;
    socialMediaName: string;
    socialMediaLink: string;
    isVendorBillingAttached: boolean;
    createdBy: string;
    createdByModelName: string;
    createdByType: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
    vendorBilling: string;
  };
  payments: IPayment[];
  ledgers: ILedger[];
  invoices: IInvoice[];
  billingEntity: BillingEntity;
  aggregates: IAggregate;
}

export interface ILedger {
  createdBy?: IEntryCreate;
  createdAt?: string;
  _id: string;
  briefInfluencerId: string;
  briefInfluencer: string;
  campaignInfluencer: string;
  campaignInfluencerId: string;
  vendorBilling: string;
  vendorBillingId: string;
  briefId: string;
  brief: string;
  influencerId: string;
  influencer: string;
  ledgerType: string;
  type: "Credit" | "Debit";
  subType: string;
  remarks: string;
  isGst: boolean;
  isTds: boolean;
  gstPercentage: number;
  gstAmount: number;
  tdsPercentage: number;
  tdsAmount: number;
  __v: number;
  baseCost: number;
}
export interface IPayment {
  linkedInvoiceId?: string;
  isBankVerificationRequired: boolean;
  bankVerificationStatus: BANKVERIFICATIONSTATUS;
  bankVerification?: IBankVerification;
  bankVerificationToken?: {
    _id?: string;
    token?: string;
  };
  bankVerificationLink?: string;
  createdBy?: IEntryCreate;
  createdAt?: string;
  _id: string;
  briefInfluencerId: string;
  briefInfluencer: string;
  campaignInfluencer: string;
  campaignInfluencerId: string;
  remarks: string;
  vendorBilling: string;
  vendorBillingId: string;
  briefId: string;
  brief: string;
  influencerId: string;
  influencer: string;
  paymentType: string;
  type: "Credit" | "Debit";
  subType: string;
  isAuto: boolean;
  amount: number;
  payoutStatus: string;
  externalPayoutStatus: string;
  externalUtrNo?: string;
  externalPayoutRejectionReason?: string;
  __v: number;
  linkedInvoice?: IInvoice | null;
  payout?: {
    payoutLink: string;
    status: string;
    vendorType: string;
    _id: string;
    externalStatus?: string;
  };
  payoutId?: string;
  proofUrl?: string;
}
export interface IAggregate {
  ledger: {
    base: number;
    gst: number;
  };
  payments: {
    base: {
      total: number;
      left: number;
    };
    gst: {
      total: number;
      left: number;
    };
  };
  invoices: {
    base: number;
    left: number;
  };
}

export interface ICreateLedger {
  campaignInfluencerId: string;
  type: "Credit" | "Debit";
  baseCost: number;
  gstPercentage: number;
  tdsPercentage: number;
  remarks: string;
  hasGst: boolean;
  hasTds: boolean;
}

export interface IEditLedger extends ICreateLedger {
  ledgerId: string;
}

export interface ICreateManualPayout {
  campaignInfluencerId: string;
  type: "Credit" | "Debit";
  remarks: string;
  amount: number;
  proofUrl?: string;
}

export interface ICreateManualGstPayout extends ICreateManualPayout {
  linkedInvoiceId?: string;
}
export interface ICreateManualPayoutBank extends Omit<
  ICreateManualPayout,
  "type"
> {
  amountType: AmountType;
  linkedInvoiceId?: string;
}
export interface ICreatePayout {
  campaignInfluencerId: string;
  remarks: string;
  amount: number;
}

export enum AmountType {
  Base = "Base",
  GST = "Gst",
}

export interface IEditManualPayout extends ICreateManualPayout {
  vendorPaymentId: string;
}

export interface IEditManualGstPayout extends ICreateManualGstPayout {
  vendorPaymentId: string;
}
export interface ICreateGstPayout {
  campaignInfluencerId: string;
  linkedInvoiceId: string;
  remarks: string;
  amount: number;
}

export interface ICreateInvoice {
  campaignInfluencerId: string;
  type: "Invoice" | "Credit-Note";
  baseCost: number;
  gstPercentage: number;
  tdsPercentage: number;
  remarks: string;
  url: string;
  invoiceDate: string;
  hasGst: boolean;
  hasTds: boolean;
}

export interface IEditInvoice extends ICreateInvoice {
  invoiceId: string;
}

export interface IInvoice {
  _id: string;
  briefInfluencerId: string;
  briefInfluencer: string;
  campaignInfluencer: string;
  campaignInfluencerId: string;
  vendorBilling: string;
  vendorBillingId: string;
  briefId: string;
  brief: string;
  influencerId: string;
  influencer: string;
  invoiceType: string;
  type: "Invoice" | "Credit-Note";
  remarks: string;
  url: string;
  baseCost: number;
  isGst: boolean;
  isTds: boolean;
  gstPercentage: number;
  gstAmount: number;
  tdsPercentage: number;
  tdsAmount: number;
  invoiceDate: string;
  createdBy?: IEntryCreate;
  createdAt?: string;
  __v: number;
}

export interface IEntryCreate {
  firstName?: string;
  lastName?: string;
}

export interface IbriefDynamicFacets {
  [key: string]: { _id: string; count: number }[];
}

export interface ICampaignDeliverableTaskTimelineEntry {
  status: BriefDeliverableStatusEnum;
  createdBy: ICreatedBy;
  uploaderApproverType: UploaderType;
  createdByModelName: string;
  createdByType: string;
  createdAt: string; // ISO datetime string
  formId?: string;
  form?: ICampaignDeliverableTaskForm;
  taskName?: string;
}

export interface ICampaignDeliverableTaskHistory {
  _id: string;
  taskName: string;
  status: BriefDeliverableStatusEnum;
  approvalPendingOn?: UploaderType;
  formId?: string;
  form?: ICampaignDeliverableTaskForm;
  timeline: ICampaignDeliverableTaskTimelineEntry[];
}

export interface ICampaignDeliverableTask {
  _id: string;
  taskName: string;
  status: BriefDeliverableStatusEnum;
  timeline: ICampaignDeliverableTaskTimelineEntry[];
  historyTask: ICampaignDeliverableTaskHistory[];
  approvalPendingOn?: UploaderType;
  form?: ICampaignDeliverableTaskForm;
  formId?: string;
  deadline?: string;
  type: BriefDeliverableTaskTypeEnum;
  scriptingId?: string;
  endAt?: string;
  startAt?: string;
}

export interface ICampaignDeliverableTaskForm {
  _id: string;
  contentUrls: ICampaignDeliverableTaskContent[];
  description?: string;
  commentsFromCreator?: string;
  createdAt: string;
  updatedAt: string;
  type: BriefDeliverableTaskTypeEnum;
  title?: string;
  briefDeadline?: string;
  draftDeadline?: string;
  liveDeadline?: string;
  reviewForm?: ICampaignDeliverableTaskReviewForm[];
  scripting?: string;
  scriptingId?: string;
  reportMediaId?: string[];
  reportMedia: ReportMedia[];
  drafting: IDrafting;
  draftingId?: string;
  draftVersionId?: string;
  history?: IBriefFormHistoryEntry[];
}

export interface IBriefFormHistoryEntry {
  createdOn: string;
  name: string;
  remarks: string[];
  userId: string;
  userType: string;
  user: ICreatedBy;
  userModelName: string;
}

export interface ICampaignDeliverableTaskReviewForm {
  remarks?: string;
  createdBy?: string;
  createdByModelName?: UploaderType;
  createdByType?: string;
  createdAt: Date;
  approverType?: UploaderType;
}

export interface ICampaignDeliverable {
  _id: string;
  deliverableType: string;
  tasks: ICampaignDeliverableTask[];
  lastestTask?: ICampaignDeliverableTask;
  timeline?: ICampaignDeliverableTaskTimelineEntry[];
}

export interface ICampaignDeliverableTaskUpload {
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskId: string;
  contentUrls: ICampaignDeliverableTaskContent[];
  description?: string;
  title?: string;
  scriptDeadline?: string;
  draftDeadline?: string;
  liveDeadline?: string;
  commentsFromCreator?: string;
}
export interface ICampaignDeliverableTaskApprove {
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskId: string;
  remarks?: string;
}

export interface ICampaignDeliverableTaskContent {
  url: string;
  type: BriefDeliverableTaskUploadTypeEnum;
  name?: string;
}

export interface IScriptingTimeframe {
  sceneName?: string;
  // duration: number;
  visuals: string;
  // audio: string;
  // supers: string;
  _id?: string;
  createdBy?: ICreatedBy;
  createdByModelName?: string;
  createdByType?: string;
}

export interface IScriptingCommentReply {
  _id?: string;
  repliedBy?: ICreatedBy;
  repliedByModelName?: string;
  repliedByType?: string;
  text?: string;
  createdAt?: string; // ISO datetime string
  status?: string; // client side status: "pending" | "error" | "success"
  clientReplyId?: string;
}

export interface IScriptingComment {
  _id?: string;
  createdBy?: ICreatedBy;
  createdByModelName?: string;
  createdByType?: string;
  createdAt?: string; // ISO datetime string
  commentedOn?: ScriptCommentedOnEnum;
  threadId?: string;
  quotedText?: string;
  timeframe?: IScriptingTimeframe;
  comment?: string;
  replies?: IScriptingCommentReply[];
  status?: string; // client side status: "pending" | "error" | "success"
  clientCommentId?: string;
  isResolved?: boolean;
}

export interface IScriptingUploadGeneratedAiParsedResponseItem {
  sceneName: string;
  duration: number;
  visuals: string;
  audio: string;
  supers: string;
}

export interface IScriptingUploadGeneratedAi {
  status: "Pending" | "Success" | "Failed" | string;
  modelUsed?: string;
  createdAt: string; // ISO
  createdBy: string;
  createdByModelName: string;
  createdByType: string;
  _id: string;
  error?: string | null;
  parsedResponse?: {
    title?: string;
    concept?: string;
    timeframe?: IScriptingUploadGeneratedAiParsedResponseItem[];
  };
  updatedAt?: string;
}

export interface IScriptingUpload {
  type: "Url" | string;
  url: string;
  generatedAi?: IScriptingUploadGeneratedAi;
  _id: string;
}

export interface IScripting {
  _id?: string;
  history: IBriefFormHistoryEntry[];

  title: string;
  concept: string;
  timeframe: IScriptingTimeframe[];
  comments: IScriptingComment[];
  uploadedScriptHistory?: IScriptingUpload[];
  uploadedScript?: IScriptingUpload;
  campaignInfluencerId?: string;
  campaignTaskId?: string;
  briefId?: string;
  brief?: IBrief;
  brand: string;
  brandId: string;
  createdBy: ICreatedBy;
  createdByModelName: string;
  createdByType: string; // API returns strings like 'admin-user'
  createdAt: string; // ISO datetime string
  updatedAt: string; // ISO datetime string
  __v?: number;
  aiWorkspaceMasterPrompt: string;
  aiWorkspaceMasterPromptId: string;
  aiWorkspaceRequestId: string;
}

export interface IUpdateScripting {
  scriptId: string;
  concept?: string;
  title?: string;
}

export interface IUploadScripting {
  scriptId: string;
  fileUrl: string;
}

export interface ICreateScriptTimeframe {
  scriptId: string;
  sceneName?: string;
  duration?: number;
  visuals?: string;
  audio?: string;
  supers?: string;
}

export interface IEditScriptTimeframe extends ICreateScriptTimeframe {
  timeframeId: string;
}

export interface IDeleteScriptTimeframe {
  scriptId: string;
  timeframeId: string;
}

export interface ICreateScriptComment {
  scriptId: string;
  comment: string;
  commentedOn?: ScriptCommentedOnEnum;
  timeframeId?: string;
  threadId?: string;
  quotedText?: string;
}
export interface IDeleteScriptComment {
  scriptId: string;
  commentId: string;
}

export interface IReplyToScriptComment {
  scriptId: string;
  commentId: string;
  reply: string;
  clientReplyId?: string;
  status?: string;
}

export interface IResolveScriptComment {
  scriptId: string;
  commentId: string;
  isResolved: boolean;
}

export enum ScriptCommentedOnEnum {
  Concept = "Concept",
  Timeframe = "Timeframe",
  General = "General",
  Thread = "Thread",
}

export interface IGetScriptingResponse {
  script: IScripting;
  campaignInfluencerTaskState?: ICampaignDeliverableTask;

  campaignInfluencerDeliverableState?: ICampaignDeliverable;
  campaignInfluencer?: BriefCampaignInf;
}

export interface IGetUnattachedScriptingResponse {
  _id?: string;
}

export interface IGetScriptComplianceResponse {
  feedbackSummary?: string;
  feedbackSummaryRequestedAt?: Date;
  feedbackSummaryGeneratedAt?: Date;
  feedbackSummaryStatus?: keyof typeof scriptingStatusConst;
  feedbackSummaryError?: any;

  complianceCheckRequestedAt?: Date;
  complianceCheckGeneratedAt?: Date;
  complianceCheckStatus?: keyof typeof scriptingStatusConst;
  complianceCheckReport?: IComplianceReport;
  complianceCheckError?: any;
}

export interface ICreatedBy {
  emailId: string;
  firstName: string;
  lastName: string;
  _id: string;
  profilePicUrl?: string;
}

export interface IBriefCampaignInfluencerSearchRequestV2 {
  searchQuery?: string;
  briefId: string;
  isVendorBillingAttached?: boolean;
  isBrandOrCreatorDeclined?: boolean;
  assignedOperationsPoc?: string[];
  deliverables?: {
    [deliverableId: string]: {
      tasks?: {
        [taskId: string]: {
          statuses?: BriefDeliverableStatusEnum[];
          approvalPendingOn?: UploaderType[];
        };
      };
      lastestTask?: {
        statuses?: BriefDeliverableStatusEnum[];
        approvalPendingOn?: UploaderType[];
      };
    };
  };

  financeFlags?: {
    invoice?: {
      statuses?: FinanceInvoiceFlags[];
    };
    basePayout?: {
      statuses?: FinanceBasePayoutFlags[];
    };
    gstPayout?: {
      statuses?: FinanceGstPayoutFlags[];
    };
  };

  pageNo?: number;
  limit?: number;

  sortByDynamicFields?: {
    [key: string]: SORT_ORDER;
  };
  dynamicFields?: {
    [key: string]:
      | {
          equalTo: string[];
        }
      | {
          range: {
            from?: number;
            to?: number;
          };
        };
  };
  pendingReviewActions?: {
    agency?: ICampaignPendingReviewAction;
    brand?: ICampaignPendingReviewAction;
  };
}

export interface ICampaignPendingReviewAction extends Partial<{
  completedBrief: boolean;
  completedScript: boolean;
  completedDraft: boolean;
  completedLive: boolean;
  pendingApprovalOnBrief: boolean;
  pendingApprovalOnScript: boolean;
  pendingApprovalOnDraft: boolean;
  pendingApprovalOnLive: boolean;
  editsRequestedOnBrief: boolean;
  editsRequestedOnScript: boolean;
  editsRequestedOnDraft: boolean;
  editsRequestedOnLive: boolean;
  approvedOnBrief: boolean;
  approvedOnScript: boolean;
  approvedOnDraft: boolean;
  approvedOnLive: boolean;
}> {}

export interface ICampaignPendingReviewActionCount extends Partial<{
  completedBrief: number;
  completedScript: number;
  completedDraft: number;
  completedLive: number;
  pendingApprovalOnBrief: number;
  pendingApprovalOnScript: number;
  pendingApprovalOnDraft: number;
  pendingApprovalOnLive: number;
  editsRequestedOnBrief: number;
  editsRequestedOnScript: number;
  editsRequestedOnDraft: number;
  editsRequestedOnLive: number;
  approvedOnBrief: number;
  approvedOnScript: number;
  approvedOnDraft: number;
  approvedOnLive: number;
  total: number;
  briefInfluencerCount: number;
}> {}

export interface ISearchFacets {
  assignedOperationsPoc: {
    _id: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
  }[];
}
export interface IGetTaskForm extends BriefCampaignInf {
  taskForm: ICampaignDeliverableTaskForm;
}

export interface IGetTaskFormResponse {
  data: IGetTaskForm[];
  pagination: Pagination;
}

export interface IGetTaskFormRequest {
  pageNo: 1;
  limit: 100;
  searchQuery: string;
  deliverableConfigRefId: string;
  taskConfigRefId: string;
  briefId: string;
}

export enum BANKVERIFICATIONSTATUS {
  Pending = "Pending",
  Verified = "Verified",
  Failed = "Failed",
}

export interface IBankIfscDetails {
  bank: string;
  ifsc: string;
  micr: number;
  nbin: string | null;
  address: string;
  city: string;
  state: string;
  branch: string;
  ifsc_subcode: string;
  category: string | null;
  swift_code: string;
}

export interface IBankVerificationResponse {
  reference_id: number;
  name_at_bank: string;
  bank_name: string;
  utr: string | null;
  city: string;
  branch: string;
  micr: number;
  name_match_score: number | null;
  name_match_result: string | null;
  account_status: string;
  account_status_code: string;
  ifsc_details: Partial<IBankIfscDetails>;
}

export interface IBankVerification {
  _id: string;

  bankAccountNumber: string;
  ifscCode: string;
  bankAccountName: string;
  status: BANKVERIFICATIONSTATUS;
  externalStatus: ExternalPayoutStatus;
  createdBy: string;
  createdByModelName: string;
  createdByType: string;
  verificationSource: string;
  failureResponse?: string;
  verificationResponse?: Partial<IBankVerificationResponse>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface IPayoutInfluencer {
  _id: string;
  briefInfluencerId: string;
  briefInfluencer: string;
  campaignInfluencer: {
    _id: string;
    brief: {
      _id: string;
      name: string;
    };
    influencerName: string;
    socialMediaName: string;
    socialMediaLink: string;
  };
  campaignInfluencerId: string;
  remarks: string;
  vendorBilling: {
    _id: string;
    phoneNumber?: string;
    countryCode?: string;
    name: string;
    emailId?: string;
  };
  bankVerification?: Partial<IBankVerification>;
  bankVerificationToken?: {
    _id?: string;
    token?: string;
  };
  externalUtrNo?: string;
  vendorBillingId: string;
  briefId: string;
  brief: string;
  influencerId: string;
  influencer: string;
  paymentType: string;
  type: "Credit" | "Debit";
  subType: string;
  isAuto: boolean;
  amount: number;
  payoutStatus: PAYOUTSTATUS;
  externalPayoutStatus: ExternalPayoutStatus;
  updatedBy: string;
  createdBy: string;
  updatedByModelName: string;
  createdByModelName: string;
  updatedByType: string;
  createdByType: string;
  isBankVerificationRequired: boolean;
  bankVerificationStatus: BANKVERIFICATIONSTATUS;
  createdAt: string;
  updatedAt: string;
  __v: number;
  bankVerificationTokenId: string;
  // vendorPaymentId: string;
  bankVerificationExportId?: string;
  externalPayoutRejectionReason?: string;
  vendorPaymentId?: string;
  externalPaymentDate?: string;
  bankVerificationLink?: string;
}

export enum ExternalPayoutStatus {
  Pending = "Pending",
  Queued = "Queued",
  Processing = "Processing",
  Success = "Success",
  Failed = "Failed",
  Cancelled = "Cancelled",
}

export enum BankExportStatus {
  PendingExport = "Pending-Export",
  PendingUpload = "Pending-Upload",
  ExportFailed = "Export-Failed",
  Success = "Success",
  Cancelled = "Cancelled",
}

export enum PAYOUTSTATUS {
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed",
}

export interface IGetPayoutsRequest {
  pageNo: number;
  limit: number;
  searchQuery: string;
  externalPayoutStatuses: ExternalPayoutStatus[];
  // bankVerificationStatuses: BANKVERIFICATIONSTATUS[];
  isBankVerificationRequired: true;
  briefIds: string[];
}

export interface IGetPayoutFacetsResponse {
  externalPayoutStatus: {
    [key in keyof typeof ExternalPayoutStatus]: number;
  };
  bankVerificationStatus: {
    [key in keyof typeof BANKVERIFICATIONSTATUS]: number;
  };
}
export interface IGetPayoutsResponse {
  data: IPayoutInfluencer[];
  pagination: Pagination;
}

export interface ICreateDownloadPayoutsQueue {
  externalPayoutStatuses: ExternalPayoutStatus[];
  briefIds: string[];
  createdAt?: {
    from: string;
    to: string;
  };
}

export interface IGetCampaignInfluencersFacetsResponse {
  taskCounts: {
    _id: string;
    completedCount: number;
    count: number;
  }[];
  actionPendingOnBrand: number;
  anyTaskCompletedCampaignInfluencers: number;
}

export interface IExportPaymentsRequest {
  vendorPaymentIds: string[];
}

export interface ICheckBankDetailsRequest {
  vendorPaymentId: string;
  bankAccountNumber: string;
  ifscCode: string;
}

export interface IRegisterBankDetailsRequest {
  vendorPaymentId: string;
}

export interface IgetSearchPaymentsRequest {
  pageNo: number;
  limit: number;
  statuses: BankExportStatus[];
}

export interface IVendorPaymentBankExport {
  _id: string;
  vendorPaymentIds: string[];
  fileNo: number;
  totalAmount: number;
  status: BankExportStatus;
  createdAt: string;
  updatedAt: string;
  __v?: number;
  exportGoogleSheetId?: string;
  score?: number;
}

export interface IgetSearchPaymentsResponse {
  data: IVendorPaymentBankExport[];
  pagination: Pagination;
}

export interface IParseExportPaymentsResponse {
  status: "success" | "failed";
  vendorPaymentId: string;
  vendorPaymentStatus: "Success" | "Failed";
  rejectionReason?: string;
  utrNo?: string;
  message?: string;
  errors?: string[];
  vendorPayment?: {
    vendorBilling?: {
      name?: string;
    };
    amount?: number;
  };
  rowNumber?: number;
  Amount?: string;
  "Beneficiary Name"?: string;
  paymentDate?: string;
}

export interface ISyncExportRequest {
  vendorPaymentBankExportId: string;
  actionOnVendorPayments: {
    vendorPaymentId: string;
    vendorPaymentStatus: "Success" | "Failed";
    rejectionReason?: string;
    utrNo?: string;
  }[];
}
